import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCog, FaSort, FaSortUp, FaSortDown, FaSearch, FaTimes, FaChevronDown, FaFilter } from 'react-icons/fa';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import './TableView.css'; // Estilos mínimos restantes (action buttons, etc.)
import { normalizarTexto } from '../utils/texto';

const DraggableHeader = ({ header, table }) => {
  const isSortable = header.column.getCanSort();
  const sorted = header.column.getIsSorted();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: header.id,
  });

  const style = {
    transform: transform ? `translateX(${transform.x}px)` : undefined,
    transition,
    width: header.column.getSize(),
  };

  const handleClick = (e) => {
    if (!e.target.closest('.drag-handle')) {
      header.column.getToggleSortingHandler()?.(e);
    }
  };

  return (
    <th
      ref={setNodeRef}
      style={style}
      className={isSortable ? 'cursor-pointer select-none' : ''}
    >
      <div className="flex items-center gap-2 w-full">
        <div
          className="drag-handle cursor-grab text-gray-300 opacity-0 hover:opacity-100 transition-opacity"
          {...attributes}
          {...listeners}
        >
          ⋮⋮
        </div>
        <div onClick={handleClick} className="flex items-center gap-2 flex-1 whitespace-nowrap">
          {flexRender(header.column.columnDef.header, header.getContext())}
          {isSortable && (
            <span className="inline-flex items-center text-gray-400 ml-auto pl-1">
              {sorted === false && <FaSort size={12} />}
              {sorted === 'asc' && <FaSortUp size={12} />}
              {sorted === 'desc' && <FaSortDown size={12} />}
            </span>
          )}
        </div>
      </div>
    </th>
  );
};

const TableView = ({
  data,
  columns,
  loading,
  error,
  columnVisibility = {},
  setColumnVisibility = () => {},
  onFetchData,
  defaultSorting = [],
  defaultPageSize = 30,
  activeActionMenu = null,
  setActiveActionMenu = () => {},
  availableFilters = [],
  getRowClassName,
  onFilteredCountChange,
  onFilteredRowsChange,
  onFiltersChange,
  customActions,
  hideColumnSettings = false,
  // Expansion support (opt-in)
  enableExpansion = false,
  getSubRows,
  defaultExpanded = true,
}) => {
  const [sorting, setSorting] = useState(defaultSorting);
  const [globalFilter, setGlobalFilter] = useState('');
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [expanded, setExpanded] = useState(enableExpansion ? defaultExpanded : {});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [selectedSede, setSelectedSede] = useState('');
  const [selectedGerencia, setSelectedGerencia] = useState('');
  const columnSettingsRef = useRef(null);

  // Mantener el estado de paginación cuando los datos cambian
  const paginationState = useMemo(
    () => ({
      pageIndex: pagination.pageIndex,
      pageSize: pagination.pageSize,
    }),
    [pagination.pageIndex, pagination.pageSize]
  );

  const [columnOrder, setColumnOrder] = useState(() =>
    columns
      .filter(col => (col.id || col.accessorKey) && col.id !== 'acciones')
      .map(column => column.id || column.accessorKey)
  );

  // Calcular el ancho óptimo para cada columna
  const columnSizing = useMemo(() => {
    const sizing = {};
    columns.forEach(column => {
      const key = column.id || column.accessorKey;
      if (!key) return;

      // Respetar el size definido en la columna como mínimo
      const definedSize = column.size || 120;

      try {
        const headerText = typeof column.header === 'string' ?
          column.header :
          column.header?.toString() || key;
        const headerWidth = (headerText.length * 7) + 45;

        const contentWidth = data.reduce((max, row) => {
          const value = row[key];
          if (value == null) return max;
          const text = value.toString();
          const width = text.length * 7 + 16;
          return Math.max(max, width);
        }, 0);

        sizing[key] = Math.max(definedSize, headerWidth, contentWidth);
      } catch (error) {
        console.warn(`Error calculando ancho para columna ${key}:`, error);
        sizing[key] = definedSize;
      }
    });
    return sizing;
  }, [columns, data]);

  // Modificar el estado de filtros para ser dinámico
  const [filters, setFilters] = useState(
    availableFilters.reduce((acc, filter) => ({
      ...acc,
      [filter.id]: ''
    }), {})
  );

  // Obtener las opciones únicas para los filtros de manera dinámica
  const filterOptions = useMemo(() => {
    return availableFilters.reduce((acc, filter) => {
      if (filter.options) {
        return {
          ...acc,
          [filter.id]: filter.options.sort()
        };
      }
      const values = new Set();
      data.forEach(item => {
        if (item[filter.id]) {
          // Asegurarnos de que el valor se almacene en el mismo formato que se usará para filtrar
          values.add(item[filter.id].toString().trim());
        }
      });
      return {
        ...acc,
        [filter.id]: Array.from(values).sort()
      };
    }, {});
  }, [data, availableFilters]);

  // Modificar la función de filtrado personalizada
  const filterFn = useCallback((row, columnId, filterValue) => {
    if (!filterValue) return true;

    const value = row.getValue(columnId);

    // Si el valor es nulo o undefined, no hay coincidencia
    if (value == null) return false;

    // Convertir tanto el valor como el filtro a strings para la comparación
    const valueStr = normalizarTexto(value);
    const filterStr = normalizarTexto(filterValue).trim();

    // Para la búsqueda global, buscar coincidencia parcial
    if (columnId === 'global') {
      return valueStr.includes(filterStr);
    }

    // Para filtros de columna, buscar coincidencia exacta
    return valueStr === filterStr;
  }, []);

  // Modificar la configuración de la tabla
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      pagination,
      columnOrder,
      columnSizing,
      ...(enableExpansion && { expanded }),
      // Modificar los filtros de columna para incluir todos los filtros disponibles
      columnFilters: Object.entries(filters)
        .filter(([_, value]) => value)
        .map(([key, value]) => ({
          id: key,
          value: value
        }))
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onColumnOrderChange: setColumnOrder,
    ...(enableExpansion && {
      onExpandedChange: setExpanded,
      getSubRows: getSubRows,
      getExpandedRowModel: getExpandedRowModel(),
      filterFromLeafRows: true,
    }),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    filterFns: {
      custom: filterFn,
    },
    globalFilterFn: (row, columnId, filterValue) => {
      const value = row.getValue(columnId);
      if (value == null) return false;

      // Convertir tanto el valor como el filtro a strings para la comparación
      const valueStr = normalizarTexto(value);
      const filterStr = normalizarTexto(filterValue).trim();

      // Buscar coincidencia parcial para la búsqueda global
      return valueStr.includes(filterStr);
    },
    // Asegurarnos de que cada columna use nuestro filtro personalizado
    defaultColumn: {
      filterFn: 'custom',
      minSize: 80,
      maxSize: 800,
      size: 120,
    },
    enableFilters: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    // Por defecto TanStack decide si una columna es buscable según el valor de la
    // PRIMERA fila (solo string/número). Si el primer registro tiene la celda
    // vacía (ej. un equipo "Sin asignar" de primero), esa columna queda fuera de
    // la búsqueda global — bug data-dependiente que en prod ocultaba la búsqueda
    // por nombre de empleado. Forzamos que TODAS las columnas sean buscables; el
    // globalFilterFn ya ignora los valores null.
    getColumnCanGlobalFilter: () => true,
    enableSorting: true,
    autoResetPageIndex: false,
  });

  // Notificar al componente padre sobre el número de filas filtradas
  useEffect(() => {
    if (onFilteredCountChange) {
      const filteredCount = table.getFilteredRowModel().rows.length;
      onFilteredCountChange(filteredCount);
    }
  }, [table.getFilteredRowModel().rows.length, onFilteredCountChange]);

  // Notificar al componente padre sobre las filas filtradas (para exportar respetando filtros)
  useEffect(() => {
    if (onFilteredRowsChange) {
      const rows = table.getFilteredRowModel().rows.map((r) => r.original);
      onFilteredRowsChange(rows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    table.getFilteredRowModel().rows.length,
    data,
    globalFilter,
    filters,
    sorting,
    onFilteredRowsChange,
  ]);

  // Al buscar o filtrar se vuelve a la primera página. Si no, estando en la
  // página 3, una búsqueda con pocos resultados deja la tabla vacía.
  useEffect(() => {
    setPagination(prev => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [globalFilter, filters]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setColumnOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const getVisibleColumns = () => {
    // Obtener todas las columnas excepto acciones
    const regularColumns = table.getAllLeafColumns().filter(col => col.id !== 'acciones');
    // Obtener la columna de acciones
    const accionesColumn = table.getAllLeafColumns().find(col => col.id === 'acciones');

    // Retornar las columnas regulares seguidas de acciones
    return [...regularColumns, accionesColumn].filter(Boolean);
  };

  // Manejar clics fuera del panel de configuración de columnas
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showColumnSettings &&
        columnSettingsRef.current &&
        !columnSettingsRef.current.contains(event.target) &&
        !event.target.closest('.settings-button')
      ) {
        setShowColumnSettings(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnSettings]);

  // Agregar ref para el menú de acciones rápidas
  const quickActionsRef = useRef(null);

  // Manejar clics fuera del menú
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (quickActionsRef.current &&
        !quickActionsRef.current.contains(event.target) &&
        !event.target.closest('.icon-button') &&
        !event.target.closest('.quick-actions-menu')) {
        setActiveActionMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setActiveActionMenu]);

  const ColumnVisibilityControls = () => {
    const formatColumnTitle = (columnId) => {
      return columnId
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };

    const visibilityState = columnVisibility || table.getState().columnVisibility || {};
    const orderedColumnIds = Object.keys(visibilityState);
    const orderedColumns = orderedColumnIds
      .filter(id => id !== 'acciones')
      .map(id => table.getAllLeafColumns().find(col => col.id === id))
      .filter(Boolean);

    return (
      <AnimatePresence>
        {showColumnSettings && (
          <motion.div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-start justify-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowColumnSettings(false)}
          >
            <motion.div
              ref={columnSettingsRef}
              className="bg-white shadow-2xl border-l border-gray-200 h-full w-80"
              initial={{ x: 320 }}
              animate={{ x: 0 }}
              exit={{ x: 320 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center gap-3">
                  <motion.div
                    className="p-2 bg-sura-blue/10 rounded-lg text-sura-blue"
                    animate={{ rotate: [0, 180, 360] }}
                    transition={{ duration: 0.5 }}
                  >
                    <FaCog size={16} />
                  </motion.div>
                  <h3 className="font-bold text-gray-900">Columnas Visibles</h3>
                </div>
                <motion.button
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
                  onClick={() => setShowColumnSettings(false)}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaTimes size={14} />
                </motion.button>
              </div>
              <div className="p-4 space-y-1 overflow-y-auto max-h-[calc(100vh-80px)]">
                {orderedColumns.map((column, index) => (
                  <motion.label
                    key={column.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className={`
                      flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors
                      ${column.getIsVisible()
                        ? 'bg-sura-blue/5 border-2 border-sura-blue/20'
                        : 'hover:bg-gray-50 border-2 border-transparent'
                      }
                    `}
                    whileHover={{ x: 4 }}
                  >
                    <motion.div
                      className={`
                        w-5 h-5 rounded-md flex items-center justify-center border-2 transition-colors
                        ${column.getIsVisible()
                          ? 'bg-sura-blue border-sura-blue text-white'
                          : 'border-gray-300 bg-white'
                        }
                      `}
                      whileTap={{ scale: 0.9 }}
                    >
                      {column.getIsVisible() && (
                        <motion.svg
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-3 h-3"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </motion.svg>
                      )}
                    </motion.div>
                    <input
                      type="checkbox"
                      checked={column.getIsVisible()}
                      onChange={column.getToggleVisibilityHandler()}
                      className="sr-only"
                    />
                    <span className={`text-sm font-medium ${column.getIsVisible() ? 'text-sura-blue' : 'text-gray-600'}`}>
                      {formatColumnTitle(column.id)}
                    </span>
                  </motion.label>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  // Modificar la función handleFilterChange
  const handleFilterChange = (filterName, value) => {
    console.log('Filtro cambiado:', filterName, 'Nuevo valor:', value);
    const newFilters = {
      ...filters,
      [filterName]: value
    };
    setFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }

    const columnFilters = Object.entries(newFilters)
      .filter(([_, val]) => val)
      .map(([key, val]) => ({
        id: key,
        value: val
      }));

    console.log('Aplicando filtros:', columnFilters);
    table.setColumnFilters(columnFilters);
  };

  // Agregar un useEffect para monitorear los filtros
  useEffect(() => {
    const activeFilters = Object.entries(filters).filter(([_, value]) => value);
    console.log('Filtros activos:', activeFilters);
    console.log('Número total de filas:', data.length);
    console.log('Filas después del filtrado:', table.getFilteredRowModel().rows.length);

    // Verificar qué filas pasan el filtro
    const filteredRows = table.getFilteredRowModel().rows;
    console.log('Primeras 5 filas filtradas:', filteredRows.slice(0, 5).map(row => ({
      id: row.original.id,
      ...activeFilters.reduce((acc, [key]) => ({
        ...acc,
        [key]: row.original[key]
      }), {})
    })));
  }, [filters, table]);

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden flex flex-col">
      {/* Controles de tabla */}
      <div className="p-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Búsqueda mejorada */}
          <motion.div
            className="relative flex-1 min-w-[280px] max-w-[420px]"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className={`
                relative flex items-center bg-white rounded-xl border-2 overflow-hidden transition-[border-color,box-shadow] duration-300
                ${globalFilter ? 'border-sura-blue shadow-lg shadow-sura-blue/10' : 'border-gray-200 hover:border-gray-300'}
              `}
              whileHover={{ scale: 1.01 }}
            >
              <motion.div
                className="pl-4 text-gray-400"
                animate={{
                  color: globalFilter ? '#0062A5' : '#9ca3af',
                  scale: globalFilter ? 1.1 : 1
                }}
                transition={{ duration: 0.2 }}
              >
                <FaSearch size={16} aria-hidden="true" />
              </motion.div>
              <input
                type="text"
                value={globalFilter ?? ''}
                onChange={e => setGlobalFilter(e.target.value)}
                placeholder="Buscar en todos los campos…"
                aria-label="Buscar en todos los campos"
                className="flex-1 px-3 py-3 bg-transparent border-none outline-none focus-visible:ring-0 text-sm text-gray-700 placeholder-gray-400"
              />
              <AnimatePresence>
                {globalFilter && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="flex items-center gap-2 pr-3"
                  >
                    <span className="text-xs font-semibold text-sura-blue bg-sura-blue/10 px-2 py-1 rounded-full">
                      {table.getFilteredRowModel().rows.length}
                    </span>
                    <motion.button
                      onClick={() => setGlobalFilter('')}
                      aria-label="Limpiar búsqueda"
                      className="p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                      whileHover={{ scale: 1.2, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaTimes size={12} aria-hidden="true" />
                    </motion.button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>

          {/* Filtros mejorados */}
          <div className="flex flex-wrap gap-3 items-center">
            {availableFilters.length > 0 && (
              <motion.div
                className="flex items-center gap-1 text-gray-400 mr-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <FaFilter size={12} aria-hidden="true" />
                <span className="text-xs font-medium uppercase tracking-wide">Filtros</span>
              </motion.div>
            )}

            {availableFilters.map((filter, index) => (
              <motion.div
                key={filter.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <motion.div
                  className={`
                    relative flex items-center bg-white rounded-xl border-2 transition-[border-color,box-shadow] duration-300 cursor-pointer overflow-hidden
                    ${filters[filter.id] ? 'border-sura-blue shadow-md shadow-sura-blue/10' : 'border-gray-200 hover:border-gray-300'}
                  `}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <select
                    value={filters[filter.id]}
                    onChange={e => handleFilterChange(filter.id, e.target.value)}
                    className="appearance-none bg-transparent pl-4 pr-10 py-2.5 text-sm font-medium text-gray-700 outline-none cursor-pointer min-w-[140px]"
                  >
                    <option value="" className="text-gray-400">{filter.placeholder}</option>
                    {filterOptions[filter.id]?.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <motion.div
                    className="absolute right-3 pointer-events-none text-gray-400"
                    animate={{
                      rotate: filters[filter.id] ? 180 : 0,
                      color: filters[filter.id] ? '#0062A5' : '#9ca3af'
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    <FaChevronDown size={12} />
                  </motion.div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Chips de filtros activos */}
          <AnimatePresence>
            {Object.entries(filters).some(([_, value]) => value) && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex flex-wrap gap-2 items-center"
              >
                {Object.entries(filters).map(([key, value]) => {
                  if (!value) return null;
                  const filterConfig = availableFilters.find(f => f.id === key);
                  return (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, scale: 0.8, x: -10 }}
                      animate={{ opacity: 1, scale: 1, x: 0 }}
                      exit={{ opacity: 0, scale: 0.8, x: 10 }}
                      className="flex items-center gap-2 bg-sura-blue/10 text-sura-blue pl-3 pr-2 py-1.5 rounded-full text-xs font-semibold"
                    >
                      <span className="text-sura-blue/60">{filterConfig?.placeholder?.replace('Todos', '').replace('...', '').trim() || key}:</span>
                      <span>{value}</span>
                      <motion.button
                        onClick={() => handleFilterChange(key, '')}
                        className="p-0.5 rounded-full hover:bg-sura-blue/20 transition-colors"
                        whileHover={{ scale: 1.2, rotate: 90 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaTimes size={10} />
                      </motion.button>
                    </motion.div>
                  );
                })}

                <motion.button
                  className="flex items-center gap-1 text-xs font-medium text-red-500 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                  onClick={() => {
                    setFilters(
                      availableFilters.reduce((acc, filter) => ({
                        ...acc,
                        [filter.id]: ''
                      }), {})
                    );
                    table.setColumnFilters([]);
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaTimes size={10} />
                  Limpiar todo
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          {customActions && (
            <div className="flex gap-2">
              {customActions}
            </div>
          )}

          {!hideColumnSettings && (
            <motion.button
              className="settings-button p-2.5 rounded-xl bg-white border-2 border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700 transition-colors focus-visible:ring-2 focus-visible:ring-sura-blue/30 focus-visible:ring-offset-1"
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              aria-label="Configurar columnas"
              title="Configurar columnas"
              whileHover={{ scale: 1.05, rotate: 90 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaCog size={16} aria-hidden="true" />
            </motion.button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-10 h-10 rounded-full border-3 border-gray-200 border-t-sura-blue animate-spin" />
        </div>
      ) : error ? (
        <motion.div
          className="flex flex-col items-center justify-center py-16 text-red-500"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <FaTimes size={32} className="mb-3 text-red-400" />
          <p className="font-medium">{error}</p>
        </motion.div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="overflow-x-auto animate-fadeIn">
            <table className="table-standard">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    <SortableContext
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {headerGroup.headers.map((header, headerIndex) => {
                        if (header.column.id === 'acciones') {
                          return (
                            <th
                              key={header.id}
                              style={{ width: header.column.getSize() }}
                            >
                              <div className="flex items-center gap-2">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </div>
                            </th>
                          );
                        }
                        return (
                          <DraggableHeader
                            key={header.id}
                            header={header}
                            table={table}
                          />
                        );
                      })}
                    </SortableContext>
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => {
                  const className = getRowClassName ? getRowClassName(row.original, row) : '';
                  const depthClass = row.depth > 0 ? `table-sub-row table-depth-${row.depth}` : '';

                  return (
                    <tr
                      key={row.id}
                      className={`${className} ${depthClass}`.trim()}
                      data-depth={row.depth}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {cell.column.id === 'acciones' ? (
                            <div ref={quickActionsRef}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </div>
                          ) : (
                            flexRender(cell.column.columnDef.cell, cell.getContext())
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </DndContext>
      )}

      {/* Paginación mejorada */}
      <div className="px-5 py-4 flex items-center justify-between bg-gradient-to-r from-white to-gray-50 border-t border-gray-100">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-gray-100 px-3 py-1.5 rounded-lg">
            <span className="text-xs text-gray-500">Mostrando</span>
            <span className="text-sm font-bold text-sura-blue">{table.getRowModel().rows.length}</span>
            <span className="text-xs text-gray-500">de</span>
            <span className="text-sm font-bold text-gray-700">{data.length}</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Página</span>
            <span className="bg-sura-blue text-white px-2.5 py-1 rounded-lg font-bold text-xs">
              {table.getState().pagination.pageIndex + 1}
            </span>
            <span className="text-gray-500">de</span>
            <span className="font-semibold text-gray-700">{table.getPageCount()}</span>
          </div>

          <div className="flex gap-1">
            {[
              { action: () => table.setPageIndex(0), disabled: !table.getCanPreviousPage(), icon: '⟪', title: 'Primera página' },
              { action: () => table.previousPage(), disabled: !table.getCanPreviousPage(), icon: '‹', title: 'Anterior' },
              { action: () => table.nextPage(), disabled: !table.getCanNextPage(), icon: '›', title: 'Siguiente' },
              { action: () => table.setPageIndex(table.getPageCount() - 1), disabled: !table.getCanNextPage(), icon: '⟫', title: 'Última página' },
            ].map((btn, index) => (
              <button
                key={index}
                onClick={btn.action}
                disabled={btn.disabled}
                title={btn.title}
                className={`
                  w-9 h-9 flex items-center justify-center rounded-xl text-lg font-medium transition-[border-color,color,box-shadow,transform] duration-200
                  ${btn.disabled
                    ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                    : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-sura-blue hover:text-sura-blue hover:shadow-md hover:scale-110 hover:-translate-y-0.5 active:scale-95'
                  }
                `}
              >
                {btn.icon}
              </button>
            ))}
          </div>
        </div>
      </div>

      <ColumnVisibilityControls />
    </div>
  );
};

export default TableView; 